import classNames from "classnames"

import {
  useAddBundleToCart,
  useAddToCart,
  useGetContextProduct,
  useRemoveBundle,
  useRemoveProduct,
  useUpdateBundleCartQuantity,
  useUpdateProductCartQuantity,
} from "../../../../../cart/hooks"

import CircledIconButton from "@components/elements/V2/Buttons/CircledIconButton"
import PlusIcon from "@components/elements/V2/Icons/PlusIcon"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import { isPurchasableProduct, PurchasableItem } from "@hooks/V2/storyblok"
import { getFillColorClass } from "@utils/V2/color"
import React, { useCallback } from "react"

const MinusGlyph = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y="8" width="18" height="2" />
  </svg>
)

type Props = {
  purchasableItem: PurchasableItem
  accentColor: Color
  className?: string
}

export const usePurchasableActions = (purchasableItem?: PurchasableItem) => {
  const cartItem = useGetContextProduct(purchasableItem)
  const quantity = cartItem?.quantity ?? 0

  const addProductToCart = useAddToCart()
  const addBundleToCart = useAddBundleToCart()
  const updateProductQuantity = useUpdateProductCartQuantity()
  const updateBundleQuantity = useUpdateBundleCartQuantity()
  const removeProduct = useRemoveProduct()
  const removeBundle = useRemoveBundle()

  const handleIncrement = useCallback(() => {
    if (!purchasableItem) return
    if (quantity === 0) {
      if (isPurchasableProduct(purchasableItem)) {
        addProductToCart(
          purchasableItem.storyblokUuid,
          purchasableItem.sku,
          1,
          purchasableItem.storefront
        )
      } else {
        addBundleToCart(
          purchasableItem.storyblokUuid,
          purchasableItem.bundleCode,
          1,
          purchasableItem.storefront
        )
      }
      return
    }
    if (isPurchasableProduct(purchasableItem)) {
      updateProductQuantity(purchasableItem.sku, quantity + 1)
    } else {
      updateBundleQuantity(purchasableItem.bundleCode, quantity + 1)
    }
  }, [
    quantity,
    purchasableItem,
    addProductToCart,
    addBundleToCart,
    updateProductQuantity,
    updateBundleQuantity,
  ])

  const handleDecrement = useCallback(() => {
    if (!purchasableItem) return
    if (quantity <= 1) {
      if (isPurchasableProduct(purchasableItem)) {
        removeProduct(purchasableItem.sku, purchasableItem.storefront)
      } else {
        removeBundle(purchasableItem.bundleCode, purchasableItem.storefront)
      }
      return
    }
    if (isPurchasableProduct(purchasableItem)) {
      updateProductQuantity(purchasableItem.sku, quantity - 1)
    } else {
      updateBundleQuantity(purchasableItem.bundleCode, quantity - 1)
    }
  }, [
    quantity,
    purchasableItem,
    removeProduct,
    removeBundle,
    updateProductQuantity,
    updateBundleQuantity,
  ])

  return { quantity, handleIncrement, handleDecrement }
}

const NumberControls = ({ purchasableItem, accentColor, className }: Props) => {
  const { quantity, handleIncrement, handleDecrement } =
    usePurchasableActions(purchasableItem)

  const isDark = accentColor === Color.White

  return (
    <div
      className={classNames(
        "relative z-20 mt-auto flex w-full items-center gap-16",
        className
      )}
    >
      <CircledIconButton
        color={accentColor}
        style="bordered"
        onClick={handleDecrement}
        disabled={quantity === 0}
        icon={<MinusGlyph />}
      />
      <div
        className={classNames(
          "flex flex-1 items-center justify-center rounded-32 py-14",
          isDark ? "bg-white/10" : "bg-light-gray"
        )}
      >
        <Typography
          font="grotesk"
          weight="medium"
          size="body-md"
          color={accentColor}
          text={String(quantity)}
        />
      </div>
      <CircledIconButton
        color={Color.Blue}
        style="solid"
        onClick={handleIncrement}
        icon={
          <PlusIcon
            className={classNames(
              getFillColorClass(Color.White),
              "transition-transform duration-300 group-hover:rotate-90"
            )}
          />
        }
      />
    </div>
  )
}

export default NumberControls
